import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Button, 
  Divider, 
  Chip, 
  CircularProgress, 
  Alert, 
  AlertTitle,
  Tabs, 
  Tab, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Card, 
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaymentIcon from '@mui/icons-material/Payment';
import WarningIcon from '@mui/icons-material/Warning';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CodeIcon from '@mui/icons-material/Code';
import ImageIcon from '@mui/icons-material/Image';
import InfoIcon from '@mui/icons-material/Info';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';

// Import components
import ProgressTracker from './ProgressTracker';
import ThemePurchaseCTA from './ThemePurchaseCTA';
import { useAuth } from '../contexts/AuthContext';

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  let color;
  switch (status) {
    case 'completed':
      color = theme.palette.success.main;
      break;
    case 'failed':
    case 'error':
      color = theme.palette.error.main;
      break;
    case 'pending_payment':
      color = theme.palette.warning.main;
      break;
    case 'generating':
    case 'packaging':
      color = theme.palette.info.main;
      break;
    default:
      color = theme.palette.grey[500];
  }
  
  return {
    backgroundColor: color,
    color: '#fff',
  };
});

const FileIcon = ({ extension }) => {
  switch (extension) {
    case 'php':
      return <CodeIcon color="primary" />;
    case 'css':
      return <CodeIcon color="secondary" />;
    case 'js':
      return <CodeIcon color="warning" />;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'svg':
      return <ImageIcon color="success" />;
    default:
      return <InsertDriveFileIcon />;
  }
};

/**
 * PRODUCTION PROJECT DETAILS COMPONENT
 * Uses Stripe Payment Element (inline, no redirect) for live payments.
 */
const ProjectDetails = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  
  // State
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [files, setFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [filePreviewOpen, setFilePreviewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  
  // Check for payment status in URL
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    if (paymentStatus === 'success') {
      setError(null);
      // Show success message and refresh project
      setTimeout(() => {
        fetchProject();
      }, 1000);
    } else if (paymentStatus === 'cancelled') {
      setError('Payment was cancelled. You can try again when ready.');
    }
  }, [searchParams]);
  
  // Fetch project details
  useEffect(() => {
    fetchProject();
  }, [projectId]);
  
  const fetchProject = async () => {
    try {
      setLoading(true);
      
      const response = await axios.get(`/api/projects/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      const projectData = response.data.project || response.data;
      setProject(projectData);
      setLoading(false);
      
    } catch (err) {
      console.error('Failed to fetch project:', err);
      setError('Failed to load project details. Please try again later.');
      setLoading(false);
    }
  };
  
  // Fetch project files when files tab is selected
  useEffect(() => {
    if (tabValue === 1 && project) {
      fetchFiles();
    }
  }, [tabValue, project]);
  
  const fetchFiles = async () => {
    try {
      setLoadingFiles(true);
      
      const response = await axios.get(`/api/projects/${projectId}/files`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setFiles(response.data.files || []);
      setLoadingFiles(false);
    } catch (err) {
      console.error('Failed to fetch project files:', err);
      setLoadingFiles(false);
    }
  };
  
  // Determine pricing based on project mode
  const getProjectPricing = (project) => {
    if (!project) return { price: 149, planId: 'ai-custom', type: 'Custom Theme' };

    if (project.mode === 'quick') {
      return { price: 49, planId: 'individual', type: 'Quick Theme' };
    }

    return { price: 149, planId: 'ai-custom', type: 'Custom Theme' };
  };
  
  // Handle payment success – refresh project data
  const handlePaymentSuccess = async () => {
    await fetchProject();
  };

  // Handle retry generation
  const handleRetry = async () => {
    try {
      setRetrying(true);
      setError(null);
      
      const response = await axios.post(`/api/projects/${projectId}/retry`, {}, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.data.success) {
        await fetchProject();
        alert('Generation retry started! You will not be charged until it succeeds.');
      } else {
        setError(`Retry failed: ${response.data.error || 'Unknown error'}`);
      }
      
      setRetrying(false);
    } catch (err) {
      console.error('Retry error:', err);
      setError(`Retry failed: ${err.response?.data?.error || err.message}`);
      setRetrying(false);
    }
  };
  
  // Handle download
  const handleDownload = async () => {
    const isCreatorTier = user?.subscriptionTier === 'creator';
    
    if (!isCreatorTier && (project.payment_status !== 'paid' || !project.can_download)) {
      setError('Payment required. Please complete payment before downloading.');
      return;
    }
    
    try {
      setDownloading(true);
      
      const response = await axios.get(`/api/download/${projectId}`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      // Create download
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${project.themeName || project.name || 'theme'}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setDownloading(false);
      
    } catch (err) {
      console.error('Download failed:', err);
      setDownloading(false);
      
      let errorMessage = 'Download failed';
      if (err.response?.data) {
        if (err.response.data instanceof Blob) {
          const text = await err.response.data.text();
          try {
            const json = JSON.parse(text);
            errorMessage = json.error || json.message || errorMessage;
          } catch (e) {
            errorMessage = text;
          }
        } else {
          errorMessage = err.response.data.error || err.response.data.message || errorMessage;
        }
      }
      
      setError(`Download failed: ${errorMessage}`);
    }
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle delete project
  const handleDeleteProject = async () => {
    try {
      await axios.delete(`/api/projects/${projectId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      navigate('/projects');
    } catch (err) {
      console.error('Failed to delete project:', err);
      setError(`Failed to delete project: ${err.response?.data?.message || err.message}`);
    }
  };
  
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };
  
  // Render action buttons based on project status
  const renderActionButtons = () => {
    if (!project) return null;
    
    const status = project.status;
    const paymentStatus = project.payment_status || 'unpaid';
    const retryCount = project.retry_count || 0;
    const isCreatorTier = user?.subscriptionTier === 'creator';
    const pricing = getProjectPricing(project);
    
    // Payment success from URL
    if (searchParams.get('payment') === 'success') {
      return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            <AlertTitle>Payment Successful!</AlertTitle>
            Your payment was processed successfully. Your theme is now ready for download.
          </Alert>
        </Box>
      );
    }
    
    // Generation Failed (< 3 retries)
    if (status === 'failed' && retryCount < 3) {
      return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Generation Failed</AlertTitle>
            Don't worry - you won't be charged until it succeeds! 
            You have {3 - retryCount} free {retryCount === 2 ? 'retry' : 'retries'} remaining.
          </Alert>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={retrying ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={handleRetry}
            disabled={retrying}
            fullWidth
          >
            {retrying ? 'Retrying...' : `Retry Generation (Free - Attempt ${retryCount + 1}/3)`}
          </Button>
        </Box>
      );
    }
    
    // Max Retries Reached
    if (status === 'failed' && retryCount >= 3) {
      return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>Maximum Retries Reached</AlertTitle>
            This generation failed after 3 attempts. Please contact support for assistance.
          </Alert>
          
          <Button
            variant="contained"
            color="secondary"
            href="mailto:support@getwordexpress.com"
            fullWidth
          >
            Contact Support
          </Button>
        </Box>
      );
    }
    
    // Payment Required (NOT for Creator tier)
    if (status === 'completed' && !isCreatorTier && (paymentStatus === 'pending' || paymentStatus === 'unpaid' || paymentStatus === 'processing')) {
      return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Payment Required</AlertTitle>
            Your {pricing.type.toLowerCase()} is ready! Complete payment to download.
          </Alert>

          <ThemePurchaseCTA
            projectId={projectId}
            projectMode={project.mode}
            paymentStatus={paymentStatus}
            projectStatus={status}
            context="generation"
            onPaymentSuccess={handlePaymentSuccess}
          />
        </Box>
      );
    }
    
    // Ready to Download (Paid OR Creator tier)
    if (status === 'completed' && (isCreatorTier || (paymentStatus === 'paid' && project.can_download))) {
      return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
            <AlertTitle>Theme Ready!</AlertTitle>
            {isCreatorTier 
              ? `Your ${pricing.type.toLowerCase()} is ready to download. (Creator Access)` 
              : `Your ${pricing.type.toLowerCase()} is ready to download. Payment received.`}
          </Alert>
          
          <Button
            variant="contained"
            color="success"
            startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownload}
            disabled={downloading}
            fullWidth
          >
            {downloading ? 'Downloading...' : 'Download Theme'}
          </Button>
          
          {project.download_count > 0 && (
            <Typography variant="caption" color="textSecondary" align="center" display="block" sx={{ mt: 1 }}>
              Downloaded {project.download_count} {project.download_count === 1 ? 'time' : 'times'}
            </Typography>
          )}
        </Box>
      );
    }
    
    // Still Generating
    if (status === 'generating' || status === 'processing' || status === 'packaging') {
      return (
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Generation In Progress</AlertTitle>
            Your {pricing.type.toLowerCase()} is being generated. This typically takes {pricing.type === 'Quick Theme' ? '5-10' : '15-20'} minutes.
            You will only be charged once generation completes successfully.
          </Alert>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        </Box>
      );
    }
    
    return null;
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Button
            component={RouterLink}
            to="/projects"
            startIcon={<ArrowBackIcon />}
            sx={{ mb: 2 }}
          >
            Back to Projects
          </Button>
          
          <Typography variant="h4" component="h1" gutterBottom>
            Project Details
          </Typography>
        </Box>
        
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setDeleteDialogOpen(true)}
        >
          Delete Project
        </Button>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : project ? (
        <>
          <StyledPaper>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>Project Name:</Typography>
                <Typography variant="body1" paragraph>{project.name || 'N/A'}</Typography>
                
                <Typography variant="subtitle2" gutterBottom>Generation Mode:</Typography>
                <Typography variant="body1" paragraph>
                  {project.mode === 'quick' ? 'Quick Theme ($49)' : 'Custom Theme ($149)'}
                </Typography>
                
                <Typography variant="subtitle2" gutterBottom>Theme Name:</Typography>
                <Typography variant="body1" paragraph>{project.themeName || project.name || 'N/A'}</Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>Created:</Typography>
                <Typography variant="body1" paragraph>{formatDate(project.createdAt)}</Typography>
                
                <Typography variant="subtitle2" gutterBottom>Status:</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <StatusChip 
                    label={project.status ? project.status.charAt(0).toUpperCase() + project.status.slice(1) : 'Unknown'} 
                    status={project.status}
                  />
                  {project.completedAt && (
                    <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                      Completed: {formatDate(project.completedAt)}
                    </Typography>
                  )}
                </Box>
                
                {renderActionButtons()}
              </Grid>
            </Grid>
          </StyledPaper>
          
          {/* Progress Tracker */}
          {(project.status === 'generating' || project.status === 'processing' || project.status === 'packaging') && (
            <ProgressTracker projectId={projectId} initialStatus={{
              status: project.status,
              progress: project.progress,
              message: project.message
            }} />
          )}
          
          <Box sx={{ mb: 3 }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Theme Details" />
              <Tab label="Files" />
            </Tabs>
          </Box>
          
          {tabValue === 0 && (
            <StyledPaper>
              <Typography variant="h6" gutterBottom>Theme Details</Typography>
              <Divider sx={{ mb: 2 }} />
              {(() => {
                const themeName = project.themeName || project.name;
                const meta = { ...(project.metadata || {}), ...(project.themeMetadata || {}) };
                const PRIORITY_KEYS = ['layout', 'layoutType', 'colorScheme', 'primaryColor', 'secondaryColor', 'colors', 'font', 'fonts', 'typography', 'industry', 'specialization', 'selectedPages', 'pages', 'features', 'woocommerce', 'gutenberg', 'demoImport'];
                const hasValue = (v) => v !== undefined && v !== null && (typeof v !== 'string' || v !== '');
                const priorityEntries = PRIORITY_KEYS.filter(k => hasValue(meta[k]));
                const otherEntries = Object.keys(meta).filter(k => !PRIORITY_KEYS.includes(k) && hasValue(meta[k]));

                const renderValue = (val) => {
                  if (Array.isArray(val)) return val.length > 0 ? val.join(', ') : '—';
                  if (typeof val === 'object') return JSON.stringify(val);
                  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
                  return String(val);
                };

                const renderEntry = (key) => (
                  <Box key={key} sx={{ display: 'flex', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 160, textTransform: 'capitalize' }}>
                      {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}:
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {renderValue(meta[key])}
                    </Typography>
                  </Box>
                );

                return (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Theme Name</Typography>
                      <Typography variant="body1">{themeName || '—'}</Typography>
                    </Box>

                    {(priorityEntries.length > 0 || otherEntries.length > 0) ? (
                      <>
                        {priorityEntries.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Selections</Typography>
                            {priorityEntries.map(renderEntry)}
                          </Box>
                        )}
                        {otherEntries.length > 0 && (
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Additional Details</Typography>
                            {otherEntries.map(renderEntry)}
                          </Box>
                        )}
                      </>
                    ) : (
                      <Typography variant="body1" color="textSecondary">
                        No saved theme selections were recorded for this project.
                      </Typography>
                    )}
                  </>
                );
              })()}
            </StyledPaper>
          )}
          
          {tabValue === 1 && (
            <StyledPaper>
              <Typography variant="h6" gutterBottom>Theme Files</Typography>
              <Divider sx={{ mb: 2 }} />
              
              {loadingFiles ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : files.length > 0 ? (
                <List dense disablePadding>
                  {(function renderItems(items, depth) {
                    return items.map((item) => (
                      <React.Fragment key={item.path}>
                        <ListItem sx={{ pl: depth * 3 }} disableGutters>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {item.type === 'directory' ? (
                              <FolderIcon color="warning" fontSize="small" />
                            ) : (
                              <FileIcon extension={item.extension} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={item.name}
                            secondary={item.type === 'file' && item.size != null ? `${(item.size / 1024).toFixed(1)} KB` : null}
                            primaryTypographyProps={{ variant: 'body2' }}
                            secondaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                        {item.type === 'directory' && item.children && item.children.length > 0 && renderItems(item.children, depth + 1)}
                      </React.Fragment>
                    ));
                  })(files, 1)}
                </List>
              ) : (
                <Typography variant="body1" color="textSecondary" align="center">
                  No files found for this project. Files will appear here once generation is complete.
                </Typography>
              )}
            </StyledPaper>
          )}
        </>
      ) : (
        <Alert severity="error">Project not found.</Alert>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Project</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this project? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteProject} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProjectDetails;